import {useAddExpressOrderMutation, useGetExpressOrderQuery} from 'modules/products/services';
import {IProductShort} from 'modules/products/types';
import {useCallback, useContext} from 'react';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {toast} from 'react-toastify';
import {Toast} from 'components/Toast';
import toastStyle from 'components/Toast/style.module.scss';
import {hideModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {useWineshopList} from 'modules/wineshops-list/hooks/useWineshopList';
import {getWineshopsWithStockShort} from 'modules/wineshops-list/helpers';
import {IWineShop} from 'modules/wineshops-list/models';
import {ReservationModalContext} from 'modules/product-snippet/context/ReservationModal';
import {useShowAuthDialogWithContinuation} from 'modules/auth/hooks/useShowAuthDialogWithContinuation';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {useAddToCartMutation} from 'modules/cart/services';
import {actionSetAddedProduct} from 'modules/cart/slice';
import {ICartProduct} from 'modules/cart/models';
import {useGetUserQuery} from 'modules/user/services';
import {IUserData} from 'modules/user/models';
import {TAddToCartPoint} from 'modules/analytics/types/ProductSnippetAnalytics';

type TUseReservationFlowParams = {
    productData: IProductShort;
    isPrive: boolean;
    isWorker: boolean;
    isLoggedIn: boolean;
    availability: 'IN_STOCK' | 'OUT_OF_STOCK' | 'FOR_RESERVATION';
    onAddToCartClick?: (quantityDelta: number, cartQuantityAfter: number, addToCartPoint?: TAddToCartPoint) => void;
};

export const useReservationFlow = ({
    isPrive,
    isWorker,
    isLoggedIn,
    productData,
    availability,
    onAddToCartClick,
}: TUseReservationFlowParams) => {
    const dispatch = useAppDispatch();
    const expressOrderResponse = useGetExpressOrderQuery();
    const wineryTitle = expressOrderResponse?.data?.data?.titleVinoteki;
    const storesWithStockCount = productData?.storesWithStock?.length || 0;

    const isActive = !wineryTitle && !isPrive && 'FOR_RESERVATION' === availability;
    let reserveButtonText = 'Забронировать';

    if (!isWorker && !isPrive) {
        reserveButtonText = 'В корзину';
    }

    const {data: user} = useGetUserQuery();
    const wineshopList = useWineshopList();
    const {openWineryPicker, openReserveModal} = useContext(ReservationModalContext);
    const {handleResetPagination} = useContext(FilterPickupTodayButtonContext);
    const showAuthDialogWithContinuation = useShowAuthDialogWithContinuation();
    const [addExpressOrder] = useAddExpressOrderMutation();
    const [addToCart] = useAddToCartMutation();

    const runReservationBranch = useCallback(
        async (wineshop: IWineShop, continuationUser: IUserData) => {
            if ('prive' === continuationUser.userGroupName || 'employee' === continuationUser.userGroupName) {
                openReserveModal(productData.code, wineshop);
                return;
            }

            try {
                const expressOrderResult = await addExpressOrder({idVinoteki: Number(wineshop.id)});

                if ('error' in expressOrderResult || !expressOrderResult.data?.ok) {
                    return;
                }

                handleResetPagination?.();

                const cartResponse = await addToCart({offerId: productData.offerId, quantity: 1}).unwrap();
                const newProduct = cartResponse.products.find((item) => item.offerId === productData.offerId);

                if (newProduct) {
                    dispatch(actionSetAddedProduct(newProduct as ICartProduct));

                    const newQty = newProduct.cartData?.quantity ?? 1;
                    onAddToCartClick?.(newQty, newQty, 'booking_flow');
                }

                toast(
                    <Toast
                        hasIcon={false}
                        className={toastStyle.notificationWide}
                        text={`Каталог отфильтрован по ${wineshop.title}, чтобы вы могли забрать весь заказ в одном месте`}
                        theme={'notification'}
                    />,
                    {className: 'notification', position: 'bottom-right'},
                );
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(hideModal(MODALS.SNIPPET_PRODUCT_PICKUP.name));
            }
        },
        [
            addExpressOrder,
            addToCart,
            dispatch,
            handleResetPagination,
            onAddToCartClick,
            openReserveModal,
            productData.code,
            productData.offerId,
        ],
    );

    const onReserve = useCallback(() => {
        const list = getWineshopsWithStockShort(productData.storesWithStock, wineshopList);

        openWineryPicker(list, (wineshop) => {
            if (!isLoggedIn) {
                dispatch(hideModal(MODALS.SNIPPET_PRODUCT_PICKUP.name));
                showAuthDialogWithContinuation((freshUser) => runReservationBranch(wineshop, freshUser));
                return;
            }

            if (user) {
                runReservationBranch(wineshop, user);
            }
        });
    }, [
        dispatch,
        isLoggedIn,
        openWineryPicker,
        productData.storesWithStock,
        runReservationBranch,
        showAuthDialogWithContinuation,
        user,
        wineshopList,
    ]);

    return {
        isReservationFlowActive: isActive,
        storesWithStockCount,
        reserveButtonText,
        onReserve,
    };
};
